import {match} from 'ts-pattern';

import type {ColumnResponseBody} from '../types/Api';
import type {EagColumn, EagFilterDefinition} from '../types/EagColumn';

type ColumnFilter = NonNullable<ColumnResponseBody['filter']>;

const mapQuickFilters = (qfs: Array<{value: string; label: string}> | undefined) =>
  qfs?.map((qf) => ({value: qf.value, label: qf.label}));

const mapFilterToEagFilterDefinition = (filter: ColumnFilter): EagFilterDefinition | null =>
  match(filter)
    .with({type: 'TEXT'}, (f) => ({
      type: 'TEXT' as const,
      isDisabled: f.isDisabled,
      quickFilters: mapQuickFilters(f.quickFilters),
    }))
    .with({type: 'SWITCH'}, (f) => ({
      type: 'SWITCH' as const,
      isDisabled: f.isDisabled,
      quickFilters: mapQuickFilters(f.quickFilters),
    }))
    .with({type: 'DATE'}, (f) => ({
      type: 'DATE' as const,
      isDisabled: f.isDisabled,
      quickFilters: mapQuickFilters(f.quickFilters),
      min: f.min ?? undefined,
      max: f.max ?? undefined,
      relativeDates: f.relativeDates?.map((rd) => ({value: rd.value, label: rd.label})),
    }))
    .with({type: 'DATE_RANGE'}, (f) => ({
      type: 'DATE_RANGE' as const,
      isDisabled: f.isDisabled,
      quickFilters: mapQuickFilters(f.quickFilters),
      min: f.min ?? undefined,
      max: f.max ?? undefined,
      relativeDates: f.relativeDates?.map((rd) => ({value: rd.value, label: rd.label})),
      relativeInputs: f.relativeInputs?.map((ri) => ({value: ri.value, label: ri.label})),
    }))
    .with({type: 'DATE_TIME_RANGE'}, (f) => ({
      type: 'DATE_TIME_RANGE' as const,
      isDisabled: f.isDisabled,
      quickFilters: mapQuickFilters(f.quickFilters),
      min: f.min ?? undefined,
      max: f.max ?? undefined,
      // Same mapping as DATE_RANGE: the wire variant carries both (definition.ts),
      // and DateTimeRangeFilter renders them — dropping them here silently hid
      // every BE-provided relative chip/input on date-TIME filters.
      relativeDates: f.relativeDates?.map((rd) => ({value: rd.value, label: rd.label})),
      relativeInputs: f.relativeInputs?.map((ri) => ({value: ri.value, label: ri.label})),
    }))
    .with({type: 'MULTI_CHOICE'}, (f) => ({
      type: 'MULTI_CHOICE' as const,
      isDisabled: f.isDisabled,
      quickFilters: mapQuickFilters(f.quickFilters),
      options: f.options?.map((opt) => ({key: opt.key, label: opt.label})),
      multiple: f.multiple,
    }))
    .with({type: 'MULTI_CHOICE_ASYNC'}, (f) => ({
      type: 'MULTI_CHOICE_ASYNC' as const,
      isDisabled: f.isDisabled,
      quickFilters: mapQuickFilters(f.quickFilters),
      suggestId: f.suggestId,
    }))
    .with({type: 'CHIPS'}, (f) => ({
      type: 'CHIPS' as const,
      isDisabled: f.isDisabled,
      quickFilters: mapQuickFilters(f.quickFilters),
      options: f.options?.map((opt) => ({key: opt.key, label: opt.label})),
    }))
    .with({type: 'CHECKBOX'}, (f) => ({
      type: 'CHECKBOX' as const,
      isDisabled: f.isDisabled,
      quickFilters: mapQuickFilters(f.quickFilters),
    }))
    // SLIDER / SLIDER_RANGE forward the FULL slider config, like the ag-grid
    // datagrid does (filterParams: {...filter}). steps drives thumb snapping when
    // stepSize is null; markers/markerSize drive the tick row — dropping any of
    // them makes the widget behave differently from its datagrid counterpart.
    .with({type: 'SLIDER'}, (f) => ({
      type: 'SLIDER' as const,
      isDisabled: f.isDisabled,
      quickFilters: mapQuickFilters(f.quickFilters),
      min: f.min ?? undefined,
      max: f.max ?? undefined,
      minOpen: f.minOpen ?? undefined,
      maxOpen: f.maxOpen ?? undefined,
      stepSize: f.stepSize ?? undefined,
      steps: f.steps ?? undefined,
      markerSize: f.markerSize ?? undefined,
      markers: f.markers ?? undefined,
      decimalsAllowed: f.decimalsAllowed ?? undefined,
      inputsVisible: f.inputsVisible ?? undefined,
      unit: f.unit ?? undefined,
    }))
    .with({type: 'SLIDER_RANGE'}, (f) => ({
      type: 'SLIDER_RANGE' as const,
      isDisabled: f.isDisabled,
      quickFilters: mapQuickFilters(f.quickFilters),
      min: f.min ?? undefined,
      max: f.max ?? undefined,
      minOpen: f.minOpen ?? undefined,
      maxOpen: f.maxOpen ?? undefined,
      stepSize: f.stepSize ?? undefined,
      steps: f.steps ?? undefined,
      markerSize: f.markerSize ?? undefined,
      markers: f.markers ?? undefined,
      decimalsAllowed: f.decimalsAllowed ?? undefined,
      inputsVisible: f.inputsVisible ?? undefined,
      unit: f.unit ?? undefined,
    }))
    .with({type: 'FILE_SIZE_SLIDER'}, (f) => ({
      type: 'FILE_SIZE_SLIDER' as const,
      isDisabled: f.isDisabled,
      quickFilters: mapQuickFilters(f.quickFilters),
      min: f.min ?? undefined,
      max: f.max ?? undefined,
      minOpen: f.minOpen ?? undefined,
      maxOpen: f.maxOpen ?? undefined,
      stepSize: f.stepSize ?? undefined,
      steps: f.steps ?? undefined,
      markerSize: f.markerSize ?? undefined,
      markers: f.markers ?? undefined,
      inputsVisible: f.inputsVisible ?? undefined,
      // decimalsAllowed is intentionally omitted: file sizes are integer byte counts; the unit conversion handles display precision.
    }))
    .with({type: 'RANGE'}, (f) => ({
      type: 'RANGE' as const,
      isDisabled: f.isDisabled,
      // Same mapping as every other numeric family: RangeFilter renders the
      // sentinel chips from filterDef.quickFilters — dropping them here hid
      // every BE-provided quick filter on RANGE columns.
      quickFilters: mapQuickFilters(f.quickFilters),
    }))
    .otherwise(() => null);

export const columnToFilterDefs = (column: ColumnResponseBody): Partial<EagColumn> => {
  if (!column.filter) {
    return {};
  }

  const filterDef = mapFilterToEagFilterDefinition(column.filter);

  if (!filterDef) {
    return {};
  }

  return {filter: filterDef};
};
